<script>
	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import headerBg from './images/LP-Pickle_Ball-01.jpg';
	import pickleballPaddles from './images/LP-Pickle_Ball-02.jpg';
	import soundProofThumbnail from './images/sound-proof-YT.jpg?w=610';
</script>

<Section backgroundImage={headerBg} backgroundColor="blue">
	<h1>Pickleball — The Noise Heard Around The Neighborhood</h1>
</Section>

<Section>
	<h2>
		Pickleball's Pounding Problem: How Indow Window Inserts Can Serve as an Ace
		Against the Noise
	</h2>
	<Bubble class="my-12">
		<p>
			“It’s like having a pistol range in your backyard” <a
				rel="noopener noreferrer"
				href="https://www.nytimes.com/2023/06/30/sports/pickleball-noise-complaints-lawsuits.html"
				>laments John Mancini</a
			>, 82, from Wellesley, Massachusetts.
		</p>
		<p>
			"I would get these splitting headaches…" <a
				rel="noopener noreferrer"
				href="https://www.kpbs.org/news/quality-of-life/2023/09/05/homeowners-are-increasingly-taking-legal-action-over-pickleball-noise"
				>confides Gleyzal</a
			>, a homeowner in San Diego.
		</p>
		<p>
			"Living here is hell" <a
				rel="noopener noreferrer"
				href="https://www.nytimes.com/2023/06/30/sports/pickleball-noise-complaints-lawsuits.html"
				>shares Debbie Nagle</a
			>, 67, from Scottsdale, Arizona.
		</p>
	</Bubble>
	<p>
		What could be the unexpected connection between these three stories of
		domestic agony and torturous noise? It’s not new construction, dangerous
		street racing, or late night partying…
	</p>
	<p>It’s Pickleball.</p>
	<p>
		This paddle sport is rapidly gaining popularity across America. Its rapid
		growth has led to a boom in court construction, often near homes. But the
		sport is facing a formidable foe: noise complaints.
	</p>
	<p>
		The incessant "pop, pop, pop" of pickleball paddles smacking balls is
		driving some neighbors to the brink, who are reporting insomnia, stress, and
		even a decline in quality of life.
	</p>
	<Image
		src={pickleballPaddles}
		alt="Image of four people playing pickle ball outside at sunset"
	/>
</Section>

<Section>
	<h2>The Thwack Heard Round the Neighborhood</h2>
	<p>
		Homeowners across the country are bearing the brunt of pickleball's sonic
		boom. They report increased anxiety and stress as the constant racket is
		grating and leads to feelings of unease and agitation. Of course, enjoying
		one's own property becomes difficult when a nearby pickleball court becomes
		the soundtrack to daily life.
	</p>
	<p>
		But why exactly is Pickleball noise so annoying while other racket sports
		like Tennis have never been involved in similar controversies?
	</p>
	<p>
		Although the decibel levels are not exceptionally high, the “pop, pop, pop”
		sound of Pickleball playing has some unique characteristics that make it
		particularly bothersome.
	</p>
	<ul>
		<li>
			<p>
				High pitch: The sound of paddles hitting the ball falls within a range
				that humans are particularly sensitive to.
			</p>
		</li>
		<li>
			<p>
				Erratic rhythm: The unpredictable nature of the game creates a constant
				barrage of sound, unlike the steady hum of traffic or lawnmowers.
			</p>
		</li>
	</ul>
</Section>

<Section>
	<h2>Serving Up Solutions: Indow Window Inserts</h2>
	<p>
		If Pickleball noise is driving you and your family mad, then you need a
		solution that provides ample noise reduction with minimal downsides.
	</p>
	<p>
		A common solution is replacing your windows, but this can be expensive and
		disruptive. Other solutions, like soundproofing curtains, can block the
		light coming through your windows and simply aren’t all that effective!
	</p>
	<p>
		Thankfully, Indow window inserts can reduce noise by up to 70%*. They offer
		a cost-effective, non-invasive, and easy-to-install solution that lets your
		windows continue to let the light into your home.
	</p>
	<Youtube
		src="https://www.youtube.com/embed/uxt6iMknNMs"
		posterImageSrc={soundProofThumbnail}
		title="Acoustic Insert Sound Test | Indow"
	/>
	<p>
		Our noise reducing inserts are made with acoustic-grade acrylic and our
		<a href="/warranties/snug-fit-guarantee">Snug-Fit Guarantee</a> ensures that your
		inserts will seamlessly fit into your window frames, creating a tight seal that
		helps to reduce noise.
	</p>
	<p>
		Additionally, Indow window inserts serve as an extra layer of insulation to
		keep your home warm in winter and cool in summer, making your home more
		energy efficient and reducing your yearly energy bills by an average of 20%!
	</p>
	<p>
		With Indow window inserts, you’ll sleep better, save money, and enjoy your
		space more than before. But don’t just take our word for it — check out some
		of thousands of <a
			rel="noopener noreferrer"
			href="https://www.reviews.io/company-reviews/store/indow1"
			>5-star reviews</a
		> to see for yourself.
	</p>
	<p>
		Say goodbye to pickleball ruckus and say hello to peace and quiet — fill out
		the form below to talk to an expert Fit Specialist today!
	</p>
</Section>

<GetAFreeEstimate />
